"use client";

import { useCompare, CompareItem } from "@/context/CompareContext";
import { cn } from "@/lib/utils";
import { GitCompareArrows } from "lucide-react";
import { useTranslation } from "react-i18next";
import { showToast } from "@/lib/toast";

interface CompareButtonProps {
  /** The listing data to add/remove from comparison */
  item: CompareItem;
  /** Visual style variant */
  variant?: "icon" | "badge" | "actionbar";
  className?: string;
}

/**
 * A toggle button to add/remove a listing from the compare list.
 * Designed to overlay on property cards.
 */
export default function CompareButton({
  item,
  variant = "icon",
  className,
}: CompareButtonProps) {
  const { addItem, removeItem, isSelected, isFull } = useCompare();
  const { t } = useTranslation("compare");
  const selected = isSelected(item.id);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (selected) {
      removeItem(item.id);
      return;
    }

    if (isFull) {
      showToast.warning(t("maxReached", "You can only compare 2 listings at a time"));
      return;
    }

    addItem(item);
  };

  if (variant === "actionbar") {
    return (
      <button
        onClick={handleClick}
        className={cn(
          "inline-flex items-center gap-2 rounded-md border px-3 h-9 text-sm font-medium transition-colors",
          selected
            ? "border-compare-muted bg-compare-light text-compare"
            : "border-input bg-background hover:bg-accent hover:text-accent-foreground",
          className
        )}
        aria-label={
          selected
            ? t("removeFromCompare", "Remove from comparison")
            : t("addToCompare", "Add to comparison")
        }
      >
        <GitCompareArrows className={cn("w-4 h-4", selected && "fill-current")} />
        <span className="hidden sm:inline">{selected ? t("comparing", "Comparing") : t("compare", "Compare")}</span>
      </button>
    );
  }

  if (variant === "badge") {
    return (
      <button
        onClick={handleClick}
        className={cn(
          "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors",
          selected
            ? "bg-compare text-compare-foreground"
            : "bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-compare-light hover:text-compare",
          className
        )}
        aria-label={
          selected
            ? t("removeFromCompare", "Remove from comparison")
            : t("addToCompare", "Add to comparison")
        }
      >
        <GitCompareArrows className="w-3.5 h-3.5" />
        <span>{selected ? t("comparing", "Comparing") : t("compare", "Compare")}</span>
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        "p-1.5 rounded-lg transition-colors",
        selected
          ? "bg-compare text-compare-foreground"
          : "bg-white/80 hover:bg-white text-gray-600 hover:text-compare",
        className
      )}
      title={selected ? t("removeFromCompare", "Remove from comparison") : t("addToCompare", "Compare")}
      aria-label={
        selected
          ? t("removeFromCompare", "Remove from comparison")
          : t("addToCompare", "Add to comparison")
      }
    >
      <GitCompareArrows className="w-4 h-4" />
    </button>
  );
}
